import { memone } from '@deqo/foundation';
import getStroke from 'perfect-freehand';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { component, property } from '../../../concepts/objects';
import { memoUsing } from '../../../concepts/objects/MemoUsing';
import { PointTupleType } from '../../../concepts/objects/PointTupleType';
import { Matrix } from '../../../platform/geom/Matrix';
import { Path } from '../../../platform/geom/Path';
import { Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { AbstractPhysical } from '../../canvas/entities/AbstractPhysical';
import { Figure, Physical, PotentialInteraction, RelativeTo, TransformHints, TransformScope } from '../../canvas/entities/Physical';

const reactionDef = {
    name: 'SketchStroke',
    implements: [Physical],
};

const computeFallbackHitArea = (spine: readonly Point[]) => {
    const area = Rect.fromPoints(spine);
    if (area.width < 10 && area.height < 10) {
        return Rect.fromCenter(area.center(), 5).toPolyline().toPath(true);
    }
    return null;
};

@component(reactionDef)
export class SketchStroke extends AbstractPhysical {

    public static toPath(spine: readonly Point[], size: number) {

        if (spine.length <= 2) {
            return SketchStroke.toStraightPath(spine, size);
        }

        const pairs = spine.map(x => x.toArray());
        const perfectStroke = getStroke(pairs, {
            size,
            smoothing: 0.6,
            thinning: 0.15,
            streamline: 0.6,
            easing: (t) => t,
            start: {
                taper: 0,
                cap: true,
            },
            end: {
                taper: 0,
                cap: true,
            },
        });

        if (!perfectStroke.length) return null;

        // use PathBuilder to create a path
        const builder = Path.builder();

        // Start with the first point
        builder.moveTo(Point.from(perfectStroke[0]).round(6));

        // Process points to create quadratic curves
        // This exactly matches the logic in toStrokePath
        if (perfectStroke.length > 1) {
            // For each point (except the last), use it as a control point
            // and the midpoint to the next point as the end point
            for (let i = 0; i < perfectStroke.length - 1; i++) {
                const [x0, y0] = perfectStroke[i];
                const [x1, y1] = perfectStroke[i + 1];

                const controlPoint = Point.from([x0, y0]).round(6);
                const endPoint = Point.from([(x0 + x1) / 2, (y0 + y1) / 2]).round(6);

                // If this is the first control point, use quadTo
                // For subsequent points, we'd ideally continue the same Q command
                // but PathBuilder doesn't support that directly
                builder.quadTo(controlPoint, endPoint);
            }
        }

        // Close the path
        const path = builder.build(true);
        return { path, style: 'fill', size };
    }

    private static toStraightPath(spine: readonly Point[], size: number) {
        return {
            path: Path.builder().moveTo(spine[0]).lineTo(spine[1]).build(true),
            style: 'stroke',
            size,
        };
    }

    @property({ type: 'string', default: '#000' })
    public color!: string;

    @property({ type: 'number', default: 5 })
    public thickness!: number;

    @property({ type: 'number', default: 1 })
    public base!: number;

    @property({ type: PointTupleType })
    public spine!: readonly Point[];

    @memoUsing('spine', 'thickness', 'base', 'color')
    public get layout() {
        const path = SketchStroke.toPath(this.spine, this.thickness * this.base);
        const color = this.color;
        const hit = computeFallbackHitArea(this.spine) ?? path.path;
        return { ...path, color, hit };
    }

    @memoUsing('spine', 'thickness', 'base', 'color')
    public get version() {
        return uuid();
    }

    protected get parentTransform(): Matrix {
        const f = this.computeFrame(this.spine);
        return Matrix.translate(f.left, f.top);
    }

    public createFigure(): Figure {
        const base = new Point(this.base, 0);
        const spine = this.localModelTransform().apply(this.spine);
        return [
            ...spine,
            spine[0].add(base),
        ];
    }

    public queryTransformHints(): TransformHints {
        return TransformHints.default;
    }

    public applyTransformation(scope: TransformScope, from: Figure, to: Figure): this {
        // Figures are always model level, so we need to apply the inverse transform of the parent to localize it.
        const mlt = this.modelLocalTransform();
        from = mlt.apply(from);
        to = mlt.apply(to);
        this.base = Point.vector(to[0], to[to.length - 1]).length();
        this.spine = to.slice(0, -1);
        return this;
    }

    public outline(to?: RelativeTo | Physical): Polyline {
        return this.bounds(to).toPolyline();
    }

    public path(to?: RelativeTo | Physical): Path {
        return this.outline(to).toPath();
    }

    public size(): Point {
        return this.computeFrame(this.spine).size();
    }

    public test(_: Point): boolean {
        return false;
    }

    public testInteraction(_: PotentialInteraction): [boolean, string] {
        return [false, 'none'];
    }

    @memone()
    private computeFrame(ps: readonly Point[]): Rect {
        return Rect.fromPoints(ps);
    }

}
